import { useTheme, Alert, Box, Link } from "@mui/material";
import React from "react";

import { useAuth } from "@/providers";
import { RobotoTypography, RobotoDivider } from "@/shared/components";
import { useOrgSecrets } from "@/shared/domain/secrets/hooks/useOrgSecrets";
import { Env } from "@/shared/environment";
import { RobotoDomainException } from "@/shared/http";

import { NewSecretForm } from "./NewSecretForm";
import { SecretsTable } from "./SecretsTable";

export const Secrets: React.FC = () => {
  const theme = useTheme();

  const { currentOrganization } = useAuth();

  const [nextToken, setNextToken] = React.useState<string | undefined>(
    undefined,
  );
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState(0);

  const [tokenMap, setTokenMap] = React.useState<Map<number, string>>(
    new Map(),
  );

  const secretsQuery = useOrgSecrets(
    currentOrganization?.org_id || "",
    pageSize,
    nextToken,
  );

  let errMsg: string | null = null;
  if (secretsQuery.isError) {
    errMsg =
      secretsQuery.error instanceof RobotoDomainException
        ? secretsQuery.error.message
        : "Failed to load secrets. Please try refreshing the page.";
  }

  return (
    <Box sx={{ maxWidth: "1200px" }}>
      <Alert severity="info" sx={{ marginBottom: theme.spacing(2) }}>
        For detailed information on creating, managing, and using secrets in
        actions, visit our{" "}
        <Link
          underline="hover"
          href={`https://${Env.docsDomain}/learn/secrets.html`}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
        >
          Secrets documentation
        </Link>
        .
      </Alert>

      <NewSecretForm />

      <RobotoDivider />

      <RobotoTypography
        variant="body1"
        sx={{
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(2),
        }}
      >
        Secrets
      </RobotoTypography>

      <SecretsTable
        errorMsg={errMsg}
        isLoading={secretsQuery.isPending}
        onPageBackward={(newPage) => {
          setNextToken(tokenMap.get(newPage));
          setPage(newPage);
        }}
        onPageForward={(newPage) => {
          setNextToken(secretsQuery.data?.next_token);
          setTokenMap((prev) => {
            const newMap = new Map(prev);
            newMap.set(newPage, secretsQuery.data?.next_token || "");
            return newMap;
          });
          setPage(newPage);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          // Start at the 0th page when changing page size
          setNextToken(undefined);
          setPage(0);
        }}
        pageNumber={page}
        pageSize={pageSize}
        secrets={secretsQuery.data?.items || []}
        totalCount={secretsQuery.data?.total_count || 0}
      />
    </Box>
  );
};
